/** @jsxImportSource @emotion/react */
import * as React from 'react';
import profileStub from '../../assets/images/profilePlaceholder.jpg';
import { css, SerializedStyles } from '@emotion/react';
import StatusButton from '../../forms/elements/StatusButton';
import { Column, Row } from '../../layout/flexbox';
import { calcAge, formatDate } from '../../language/time';
import {AgeText, CIRCULAR_RADIUS, FirstName, LastName} from './guestCardStyles';
import { GREEN_CREEK, RED_CREEK } from '../../styles/colors';
import { GuestDetails } from '../guestDetails';
import * as R from 'ramda';
import { hasPhoto } from '../guestPhoto/module';
import {CLEAR_BUTTON, CLICKABLE, UNSELECTABLE_TEXT} from '../../styles/styles';
import { getResource } from '../../language/resources/getResource';
import copy from '../../assets/icons/copyOutline.svg';
import { shrinkWrapElement } from './module';
import { GuestPaymentRegistration } from '../guestPayment/paymentRegistration';
import paymentLogo from '../../assets/icons/payWithPassGreen.svg'
import { FADE_IN_KEYFRAMES } from '../../styles/animations';

const styles = {
  copyMgIdContainer: css`
    margin-top: 3px;
    max-width: 100%
    justify-content: flex-start;
    flex: 1 1 auto;
  `,
  copyMgIdBtn: css`
    height: 20px;
    width: 25px;
    margin-bottom: -2px;
    ${UNSELECTABLE_TEXT};
    ${CLEAR_BUTTON};
    ${CLICKABLE};
  `,
  copyBtn: css`
    width: 17px; 
    height: 17px; 
    margin-botton: -2px;
  `,
  lastNameContainer: css`
    display: flex; 
    max-width:100%; 
    max-height:50%
  `,
  paymentButton: css`
    width: 100px;
    margin-top: 10px;
    background-color: transparent;
    border: none;
    cursor: pointer;
    animation: 1s ${FADE_IN_KEYFRAMES};
  `,
  paymentIcon: css`
    margin-left: -10px;
    width:100%;
    &:hover {
      opacity: 0.75;
    }
  `
}

/**
 * GuestProfileView: a component, used mostly in GuestCard, that renders a guest's name, age and profile picture.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class GuestProfileView extends React.PureComponent<{
  guest: GuestDetails;
  isLoadingPhoto: boolean;
  onProfileImgClick: () => void;
  rootCss?: SerializedStyles;
  hasHotlistedPass?: boolean;
  copyToClipboard: (mgId: string) => void;
  resources: Record<string, any>;
  dateFormat: string;
  showPaymentRegistrations?: boolean;
  onPaymentRegistrationsClick: (guestPaymentRegistration: GuestPaymentRegistration) => void;
}> {

  public componentDidMount(): void {
    shrinkWrapElement(document.getElementById(`gc-last-name-${this.props.guest.masterGuestId}`));
  }

  /**
   * Check registrations and return the one with the max expiration date
   * @param registrations 
   * @returns the registration with the max expirationdate
   */
  private getPaymentRegistrationWithMaxExpirationDate = (
    registrations: GuestPaymentRegistration[] | undefined,
  ) => {
    return registrations!.reduce((max, registration) => (new Date(registration.expirationDate) > new Date(max.expirationDate) ? registration : max));
  }

    private isExpired = () => {
    if (this.props.guest.paymentMethod)
      return new Date(this.props.guest.paymentMethod.expirationDate) < new Date()
    else 
      return false
    };

  public render = () => <Row justifyContent={'flex-end'} rootCss={this.props.rootCss}>
    <Column flexGrow={2} rootCss={css`
                           width: 200px;
                           padding-right: 10px;
                         `}>
      <FirstName
        title={this.props.guest.masterGuestId}
        id={`gc-first-name-${this.props.guest.masterGuestId}`}
        onClick={()=>this.props.copyToClipboard(this.props.guest.masterGuestId)}
      >
        {this.props.guest.firstName}
      </FirstName>
      <div css={styles.lastNameContainer}>
        <LastName css={css`max-height: 100% !important`}
          title={this.props.guest.masterGuestId}
          id={`gc-last-name-${this.props.guest.masterGuestId}`}
          onClick={()=>this.props.copyToClipboard(this.props.guest.masterGuestId)}
        >
          {this.props.guest.lastName}
        </LastName>
        <div css={styles.copyMgIdContainer}>
          <button title={getResource(this.props.resources, 'GUEST_PROFILE_COPY_MGID_TO_CLIPBOARD')} onClick={() => this.props.copyToClipboard(this.props.guest.masterGuestId)} css={styles.copyMgIdBtn}>
            <img src={copy} alt='Copy' css={styles.copyBtn} />
          </button>
        </div>
      </div>
      <AgeText
        title={formatDate(this.props.guest.dob, this.props.dateFormat)}
        id={`gc-dob-${this.props.guest.masterGuestId}`}
      >
        {getResource(this.props.resources, 'GUEST_PROFILE_YEARS_OLD', calcAge(this.props.guest.dob)) || getResource(this.props.resources, 'GUEST_PROFILE_NO_DOB')}
      </AgeText>
      {
        this.props.showPaymentRegistrations
        && this.props.guest.paymentRegistrations
        && this.props.guest.paymentRegistrations.length > 0
        && this.props.guest.paymentMethod
        && !this.isExpired()
        &&
          <button css={styles.paymentButton}
            title={getResource(this.props.resources, 'PAYMENT_REGISTRATION_VIEW')} 
            onClick={()=>this.props.onPaymentRegistrationsClick(
              this.getPaymentRegistrationWithMaxExpirationDate(this.props.guest.paymentRegistrations))}>
            <img css={styles.paymentIcon} src={paymentLogo} alt={'Payments'}
                title={getResource(this.props.resources, 'PAYMENT_REGISTRATION_VIEW')}/>
          </button>
      }
     
    </Column>

    <StatusButton rootCss={css`flex-basis: 100px; flex-shrink: 0;`}
                  handleClick={this.props.onProfileImgClick}
                  image={
                    R.path([ 'guest', 'photo', 'imgSmall' ], this.props)
                    || R.path([ 'guest', 'photo', 'imgLarge' ], this.props)
                    || profileStub
                  }
                  fillMode='fill'
                  borderRadius={CIRCULAR_RADIUS}
                  borderColor={hasPhoto(this.props.guest) ? GREEN_CREEK : RED_CREEK}
                  isLoading={this.props.isLoadingPhoto}
                  toolTipText={getResource(this.props.resources, 'GUEST_PROFILE_TOOLTIP')}
                  disable={this.props.hasHotlistedPass}
                  disableToolTip={getResource(this.props.resources, 'GUEST_PROFILE_DISABLED_TOOLTIP')}
                  borderWidth={3}
                  height={100}
                  width={100}/>
  </Row>;
}
